import React from 'react';
import 'twin.macro';
import { StaticImage } from 'gatsby-plugin-image';

import Layout from '~components/Layout';
import SEO from '~components/SEO';
import Container from '~components/Container';
import Hero from '~components/Hero';
import TableOfContents from '~components/TableOfContents';
import Heading from '~components/Heading';
import Text from '~components/Text';
import Callout from '~components/Callout';
import Blockquote from '~components/Blockquote';
import OverlapImage, { overlapImageStyles } from '~components/OverlapImage';

import Needle from '~components/Svg/Needle';
import Stethoscope from '~components/Svg/Stethoscope';
import Door from '~components/Svg/Door';
import PartialHouse from '~components/Svg/PartialHouse';

import { fsbgimg } from '~theme';

const toc = [
  {
    id: 'kensington',
    title: 'Kensington Market Overdose Prevention Site'
  },
  {
    id: 'peers-in-er',
    title: 'Peers in ER'
  },
  {
    id: 'street-survivors',
    title: 'Street Survivors'
  },
  {
    id: 'voluntary-trust-program',
    title: 'Voluntary Trustee Program'
  }
];

const UrbanPage: React.FC = () => (
  <Layout>
    <SEO title="Urban Health & Homelessness Services" desc="" />
    <Hero title="Urban Health & Homelessness Services">
      <StaticImage src="../images/man-with-a-mask.jpg" alt="" style={fsbgimg} />
    </Hero>
    <Container>
      <TableOfContents items={toc} />
      <Heading id={toc[0].id} level="h2">
        <span tw="text-gray-900">Kensington Market</span> Overdose Prevention Site
      </Heading>
      <Heading level="h3">A collision course with death</Heading>
      <Callout>
        <Text tw="my-0 text-2xl">
          Rob veered away from harmful substance use and toward a life of helping others at the
          Kensington Market Overdose Prevention Site
        </Text>
      </Callout>
      <Text>Toronto was already in the midst of an opioid crisis. COVID-19 made it worse.</Text>
      <Text>
        Overdose deaths are up a staggering 79% since the start of the pandemic. And one out of
        every six people who die are homeless, making locations like the Kensington Market Overdose
        Prevention Site (KMOPS) more essential than ever.
      </Text>
      <Text>
        At its core, KMOPS saves lives by preventing overdoses. But the value to people who use
        substances goes far beyond that. By making people feel comfortable and safe, they are more
        open to other services that improve their health: harm reduction, primary healthcare,
        housing and employment assistance, and mental health counselling. Better health goes
        hand-in-hand with increased confidence and self-worth. Rob is proof of that.
      </Text>
      <Blockquote>
        <Text tw="mt-0">
          “I was on a collision course with death. Not going to lie, I got a second chance because I
          overdosed. That made me want to work at a safe injection site and tell the world that a
          piece of fentanyl the size of a grain of sugar can kill you. And that’s what I’m doing
          today as a peer worker at KMOPS. I feel that if I can help just one person stay alive, or
          not get HIV, I’ve done my job.
        </Text>
        <Text tw="mb-0">
          "I could talk all day about the things that are better in my life now. The boost to my
          self-esteem for helping others is the most wonderful thing and I’m truly living my dream
          working here. I love what I do!”
        </Text>
      </Blockquote>
    </Container>
    <OverlapImage
      image={
        <StaticImage
          src="../images/man-looking-at-pipe-screens.jpg"
          alt=""
          style={overlapImageStyles}
        />
      }
    >
      <div tw="flex flex-col md:flex-row items-center space-x-4">
        <Needle tw="w-24 h-24 md:(w-48 h-48) my-6 md:my-0" />
        <Text tw="my-0 text-2xl">
          <strong>0</strong> overdose deaths at our overdose prevention site since it opened in 2019
        </Text>
      </div>
    </OverlapImage>
    <Container>
      <Heading id={toc[1].id} level="h2">
        <span tw="text-gray-900">Peers in the ER</span>
      </Heading>
      <Heading level="h3">Peer health</Heading>
      <Callout>
        <Text tw="text-2xl my-0">
          Peer workers like Yara improve the health of emergency room patients…and themselves
        </Text>
      </Callout>
      <Blockquote>
        <Text tw="my-0">
          “My mental health was deteriorating. I had been arrested a few days before the first
          COVID-19 lockdown and I was isolated at home, using substances more frequently. What
          changed for me was the job I got at one of the COVID recovery hotels. I worked there
          during the summer, then staff asked if I’d be interested in working in the Toronto Western
          Hospital emergency department because they knew I wanted to become a nurse. I accepted
          right away.”
        </Text>
      </Blockquote>
      <Text>
        Peer workers have become an essential cog in the Toronto Western Hospital emergency room.
        They build a rapport with patients through shared lived experiences of homelessness,
        substance use and physical or mental health issues. Their natural empathy helps validate
        patients’ concerns and makes it easier for hospital staff to provide effective treatment.
        More than that, peer workers advocate for patients with hospital staff, social workers,
        community members and partner organizations to ensure wraparound support is available.
      </Text>
      <Blockquote>
        <Text tw="my-0">
          “This job has humbled me and I take pride in the work. It has given me a full-time stable
          job. The routine helps me the most with my mental health and substance use. Since I
          started, I’ve learned to cope with my emotions without substances or harmful behaviours.
          Working here allows peers to share our stories and give hope to vulnerable people that
          need it. We help people see that there’s a way out of this dangerous cycle and we’re
          proof.
        </Text>
      </Blockquote>
    </Container>
    <OverlapImage
      image={<StaticImage src="../images/tatooed-nurse.jpg" alt="" style={overlapImageStyles} />}
    >
      <div tw="flex flex-col md:flex-row items-center space-x-4">
        <Stethoscope tw="w-24 h-24 md:(w-48 h-48) my-6 md:my-0" />
        <Text tw="my-0 text-2xl">
          Peer workers helped nearly <strong>700</strong> patients in the Toronto Western Hospital
          emergency room between November 2020 and May 2021
        </Text>
      </div>
    </OverlapImage>
    <Container>
      <Heading id={toc[2].id} level="h2">
        <span tw="text-gray-900">Street</span> Survivors
      </Heading>
      <Heading level="h3">Every step of the way</Heading>
      <Callout>
        <Text tw="text-2xl my-0">Jordan got his life back on track through Street Survivors</Text>
      </Callout>
      <Blockquote>
        <Text tw="my-0">
          “I was in and out of shelters or living on the street since I was 16, and suffering from
          drug use. I felt hopeless because each time I’d try to change, I kept failing. Thankfully,
          I got connected with Street Survivors at The Neighbourhood Group.”
        </Text>
      </Blockquote>
      <Text>
        Street Survivors helps people who are homeless or marginally-housed by giving assistance
        with government and housing applications, referrals for physical and medical healthcare or
        legal services, offering transportation, and even providing simple things like a list of
        local food banks. The program is mobile, ensuring that participants have everything they
        need at the ready.
      </Text>
      <Text>
        For Jordan, the journey from the street started in small increments, first by getting help
        with his medical appointments and prescriptions. By reaching that level of trust with staff,
        he was comfortable taking the next steps through harm reduction and relapse prevention
        programs.
      </Text>
      <Blockquote>
        <Text tw="my-0">
          “Staff helped me get through my difficulties, then got me a safe place to live. They
          followed up with visits to make sure I was ok and even gave me grocery gift cards.”
        </Text>
      </Blockquote>
      <Text>
        Today, Jordan has been clean for more than two years and works at the Alpha House recovery
        centre mentoring young people. His next goal? To build on his lived experience by getting a
        post-secondary degree and become a counsellor for people who use substances.
      </Text>
      <Blockquote>
        <Text tw="my-0">
          “It’s been a long journey but Street Survivors has been there every step of the way.”
        </Text>
      </Blockquote>
    </Container>
    <OverlapImage
      image={
        <StaticImage src="../images/smiling-black-man.jpg" alt="" style={overlapImageStyles} />
      }
    >
      <div tw="flex flex-col md:flex-row items-center space-x-4">
        <Door tw="w-24 h-24 md:(w-48 h-48) my-6 md:my-0" />
        <Text tw="my-0 text-2xl">
          <strong>3,000+</strong> hours spent annually helping people remain in their own homes
        </Text>
      </div>
    </OverlapImage>
    <Container>
      <Heading id={toc[3].id} level="h2">
        <span tw="text-gray-900">Voluntary</span> Trustee Program
      </Heading>
      <Heading level="h3">From money management to independence</Heading>
      <Callout>
        <Text tw="my-0 text-2xl">
          Chantelle sees just how much the Voluntary Trustee program can help people find stability
        </Text>
      </Callout>
      <Text>
        Hunger. Unemployment. Housing insecurity. Substance use. These are just a few of the
        barriers faced by people on the street. Factor in the minimal benefits that people on social
        assistance receive and it’s almost impossible to break the cycle of poverty. That’s where
        Trustee Case Managers like Chantelle come in.
      </Text>
      <Blockquote>
        <Text tw="mt-0">
          “It’s hard for people on government support to pay their bills or have enough for food. We
          help people navigate systems for things like securing safe housing or finding a doctor,
          all while working with them to budget for the essentials like rent, and in some cases,
          have a portion of their benefits automatically transferred to pay their bills. This
          stability allows people to focus on other things. You can see major life skills happening.
        </Text>
        <Text>
          “One of my clients had depression and anxiety. He literally told me he never knew where
          his next meal was coming from. The first time I saw him in the Corner Drop-in, I could see
          the relief in his face. He just came for the immediate services, like meals, showers and
          laundry, but as he quickly became comfortable, he joined the Voluntary Trustee program.
          Because of his improved financial situation, he found a secure place to live, and felt
          confident enough to find work as a landscaper.
        </Text>
        <Text tw="mb-0">
          “Watching people go from the street to safe housing, having a job&hellip;even grocery
          shopping on their own&hellip;it’s amazing!”
        </Text>
      </Blockquote>
    </Container>
    <OverlapImage
      image={
        <StaticImage src="../images/two-women-talking.jpg" alt="" style={overlapImageStyles} />
      }
    >
      <div tw="flex flex-col md:flex-row items-center space-x-4">
        <PartialHouse tw="w-24 h-24 md:(w-48 h-48) my-6 md:my-0" />
        <Text tw="my-0 text-2xl">
          <strong>98%</strong> of people in the Voluntary Trustee program are able to stay in their
          homes and maintain their independence
        </Text>
      </div>
    </OverlapImage>
  </Layout>
);

export default UrbanPage;
